import {
    DownloadButtonData,
    IntegrationOption,
} from "@nova/component-export-download-buttons-hover"
import { TimeFormat } from "@nova/format"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { config } from "@nova/configuration/browser"
import { GeometryType, IntegrationExportEntity } from "@nova/constants"
import { createLineKml, createPolygonKml } from "@nova/data-create-shape-kml"
import { AGOLSettings } from "@nova/db-table-integration-authorizations"
import { IntegrationType } from "@nova/schemas"
import { useIntegrationAuthorizations } from "features/data-management/integrations/useIntegrations"
import { useFullnameForUserId } from "features/data-management/users/storage"
import { useExportToCalTopo } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/caltopo/useExportToCalTopo"
import { useExportToTAK } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/tak/useExportToTAK"
import { useExportToAGOL } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/useExportToAGOL"
import {
    selectActiveWorkspaceId,
    selectWorkspaceDateFormat,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectFireById } from "features/entity-caching/fires/selectors"
import { selectLineById } from "features/entity-caching/lines/selectors"
import { selectActivatedFireId } from "features/map/control/selectors"
import { getDate } from "features/map/inspector-panel/module"
import { lineTransformer } from "integrations/geojson/transformers/line"
import { buildOAuthPopup, isAuthExpired } from "integrations/oauth/oauthHelpers"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectSelectedLayerIds } from "panels/content/side-panel/slice.new"
import { coordinateSystems } from "plugins/PDFv2/PDFAnnotations/PDFCoordinateNotations"
import { useCallback, useState } from "react"
import { download, downloadUri } from "tools/browser/download"
import { setGlobalNotificationContext } from "user/profile/slice"

export const useLineDownloadButtonData = (): DownloadButtonData => {
    const dispatch = useAppDispatch()

    const selectedLayerId = useAppSelector(selectSelectedLayerIds).at(-1)
    const id = selectedLayerId || ""
    const fireId = useAppSelector(selectActivatedFireId)
    const { name: projectName } = useAppSelector(selectFireById(fireId))!
    const integrationOptions: Record<string, IntegrationOption> = {}

    const line = useAppSelector(selectLineById(id))
    const { organizationSettingCRS } = config.use("organizationSettingCRS")

    const getFilename = useCallback(
        (end: string) => `${projectName} ${line.name}${end}`,
        [projectName, line]
    )

    const workspaceId = useAppSelector(selectActiveWorkspaceId())

    const { data: agolAuths, invalidateAuthorizationsCache: invalidateAGOL } =
        useIntegrationAuthorizations({
            authType: "oauth",
            integration: IntegrationType.agol,
            workspaceId: workspaceId,
        })

    const { data: takAuths, invalidateAuthorizationsCache: invalidateTAK } =
        useIntegrationAuthorizations({
            authType: "certificate",
            integration: IntegrationType.tak,
            workspaceId: workspaceId,
        })
    const [agolExportError, setAGOLExportError] = useState<string>("Failed to export")
    const { exportToAGOL } = useExportToAGOL()
    const { data: caltopoAuths, invalidateAuthorizationsCache: invalidateCalTopo } =
        useIntegrationAuthorizations({
            authType: "caltopo",
            integration: IntegrationType.caltopo,
            workspaceId: workspaceId,
        })
    const { exportToTAK } = useExportToTAK()
    const { exportToCalTopo } = useExportToCalTopo()

    const downloadKml = useCallback(() => {
        if (!line) return
        const kmlName = projectName + " - " + line.name
        download(
            getFilename(".kml"),
            "application/vnd.google-earth.kml+xml",
            line.geometryType === GeometryType.Polygon
                ? createPolygonKml([line], kmlName)
                : createLineKml([line], kmlName)
        )
    }, [line, getFilename, projectName])

    const downloadShp = useCallback(async () => {
        const type = line.geometryType === GeometryType.Line ? "Lines" : "Polygons"
        const geojson = lineTransformer(
            [
                {
                    name: line.name,
                    points: line.coordinates.map(({ longitude, latitude }) => [
                        longitude,
                        latitude,
                    ]),
                },
            ],
            coordinateSystems[organizationSettingCRS].proj4
        )

        const res = await fetch(
            "/api/ogr2ogr?crs=" + coordinateSystems[organizationSettingCRS].code + "&type=" + type,
            {
                method: "POST",
                body: new File([new Blob([JSON.stringify(geojson)])], `${type}.geojson`),
            }
        )
        if (res.status >= 300) {
            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to create shapefile.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        }
        const blob = await res.blob()

        await downloadUri(getFilename(".zip"), URL.createObjectURL(blob))
    }, [
        organizationSettingCRS,
        dispatch,
        getFilename,
        line.coordinates,
        line.geometryType,
        line.name,
    ])

    if (agolAuths?.at(0) && fireId && workspaceId) {
        const auth = agolAuths.at(0)
        const settings = auth?.settings as AGOLSettings
        const lastExport = auth?.lastExport?.[line.id]

        integrationOptions[`Send to ${settings.selectedWebMap?.title ?? "User Content"}`] = {
            callback: async () => {
                if (!auth) return
                if (!line) {
                    setAGOLExportError("Internal error occured")
                }

                if (isAuthExpired(auth)) {
                    try {
                        await buildOAuthPopup(IntegrationType.agol, workspaceId)
                    } catch (e) {
                        setAGOLExportError("Failed to authenticate")
                        throw e
                    }
                    await invalidateAGOL()
                    dispatch(
                        setGlobalNotificationContext({
                            message: `Connected to ${IntegrationType.agol.toUpperCase()}.`,
                            status: GlobalNotificationStatus.success,
                            timeoutInMs: 3000,
                        })
                    )
                }

                return exportToAGOL({
                    entities: [{ id: id, type: IntegrationExportEntity.Line }],
                    projectId: fireId,
                    workspaceId,
                })
                    .then(() => {
                        invalidateAGOL()
                    })
                    .catch((e) => {
                        setAGOLExportError("Failed to export")
                        throw e
                    })
            },
            successMessage: "Success",
            failureMessage: agolExportError,
            key: "agol",
            ExtraInfo: () => {
                const { fullName } = useFullnameForUserId(lastExport?.userId)
                const workspaceDateFormat = useAppSelector(selectWorkspaceDateFormat)

                return lastExport ? (
                    <div className="flex flex-col gap-2 mt-2 mb-2.5 text-gray-50 min-h-fit">
                        <small className="flex justify-between">
                            <span>Last Updated: </span>
                            <span className="mono text-gray-300">
                                {getDate(
                                    lastExport.time / 1000,
                                    workspaceDateFormat,
                                    TimeFormat.TwentyFourHour
                                )}
                            </span>
                        </small>
                        {fullName && (
                            <small className="flex justify-between">
                                Updated By: <span className="text-gray-300">{fullName}</span>
                            </small>
                        )}
                    </div>
                ) : (
                    <div></div>
                )
            },
        }
    }

    if (takAuths?.at(0) && fireId && workspaceId) {
        const auth = takAuths.at(0)
        const lastExport = auth?.lastExport?.[line.id]

        integrationOptions["Send to TAK"] = {
            callback: async () => {
                if (!auth) return
                if (!line) {
                    throw "No flight selected"
                }

                return exportToTAK({
                    entities: [{ id: id, type: IntegrationExportEntity.Line }],
                    projectId: fireId,
                    workspaceId,
                }).then(() => {
                    invalidateTAK()
                })
            },
            successMessage: "Success",
            failureMessage: "Failed",
            key: "tak",
            ExtraInfo: () => {
                const { fullName } = useFullnameForUserId(lastExport?.userId)
                const workspaceDateFormat = useAppSelector(selectWorkspaceDateFormat)

                return lastExport ? (
                    <div className="flex flex-col gap-2 mt-2 mb-2.5 text-gray-50 min-h-fit">
                        <small className="flex justify-between">
                            <span>Last Updated: </span>
                            <span className="mono text-gray-300">
                                {getDate(
                                    lastExport.time / 1000,
                                    workspaceDateFormat,
                                    TimeFormat.TwentyFourHour
                                )}
                            </span>
                        </small>
                        {fullName && (
                            <small className="flex justify-between">
                                Updated By: <span className="text-gray-300">{fullName}</span>
                            </small>
                        )}
                    </div>
                ) : (
                    <div></div>
                )
            },
        }
    }

    if (caltopoAuths?.at(0) && fireId && workspaceId) {
        const auth = caltopoAuths.at(0)
        const lastExport = auth?.lastExport?.[line.id]

        integrationOptions["Send to CalTopo"] = {
            callback: async () => {
                if (!auth) return

                return await exportToCalTopo({
                    entities: [{ id: id, type: IntegrationExportEntity.Line }],
                    projectId: fireId,
                    workspaceId,
                }).then(() => {
                    invalidateCalTopo()
                })
            },
            successMessage: "Success",
            failureMessage: "Failed",
            key: "caltopo",
            ExtraInfo: () => {
                const { fullName } = useFullnameForUserId(lastExport?.userId)
                const workspaceDateFormat = useAppSelector(selectWorkspaceDateFormat)

                return lastExport ? (
                    <div className="flex flex-col gap-2 mt-2 mb-2.5 text-gray-50 min-h-fit">
                        <small className="flex justify-between">
                            <span>Last Updated: </span>
                            <span className="mono text-gray-300">
                                {getDate(
                                    lastExport.time / 1000,
                                    workspaceDateFormat,
                                    TimeFormat.TwentyFourHour
                                )}
                            </span>
                        </small>
                        {fullName && (
                            <small className="flex justify-between">
                                Updated By: <span className="text-gray-300">{fullName}</span>
                            </small>
                        )}
                    </div>
                ) : (
                    <div></div>
                )
            },
        }
    }

    return {
        exportOptions: {
            KML: {
                callback: downloadKml,
            },
            SHP: {
                callback: downloadShp,
            },
        },
        integrationOptions,
    }
}
